/**
 * ClientOrderManager - 客户订单管理器
 * 
 * 负责客户订单的持久化存储、查询和更新
 * 与 BrokerOrderManager 架构一致，确保数据可靠性
 */

import type { Order } from '../types/trading.types';
import { storage } from '../utils/storage';
import { logger } from '../utils/logger';
import { authApi } from '../services/AuthApiService';

const STORAGE_KEY_PREFIX = 'client_order_';
const INDEX_KEY = 'client_order_index';

export class ClientOrderManager {
  private orders: Map<string, Order> = new Map();

  constructor() {
    this.loadOrders();
    logger.log('ClientOrderManager initialized');
  }

  /**
   * 从后端 API 获取客户订单并更新本地缓存
   */
  async fetchFromBackend(): Promise<void> {
    try {
      logger.log(`🔄 [ClientOrderManager] 从后端获取客户订单...`);
      
      const token = authApi.getToken();
      if (!token) {
        logger.warn('⚠️ [ClientOrderManager] 未登录，无法获取客户订单');
        return;
      }

      const response = await fetch(`/api/broker/client-orders`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      const orders = data.orders || [];
      
      // 清空旧数据
      this.orders.clear();
      
      // 添加新数据并转换为标准 Order 格式
      orders.forEach((apiOrder: any) => {
        const order: Order = {
          id: apiOrder.id,
          uid: apiOrder.userId,  // API 返回的是 userId
          brokerUid: apiOrder.username || apiOrder.userId,  // 使用 username 作为 brokerUid
          symbol: apiOrder.symbol,
          side: apiOrder.side,
          type: apiOrder.type,
          quantity: apiOrder.quantity,
          filledQuantity: apiOrder.filledQuantity || apiOrder.quantity,  // 使用 API 返回的 filledQuantity
          price: apiOrder.price,
          averagePrice: apiOrder.averagePrice || apiOrder.price,  // 使用 API 返回的 averagePrice
          status: apiOrder.status,
          leverage: apiOrder.leverage || 1,
          fee: apiOrder.takerFee || 0,
          feeRate: 0,  // 从 fee 和 quantity 计算
          createTime: new Date(apiOrder.createdAt).getTime(),
          updateTime: new Date(apiOrder.updatedAt || apiOrder.createdAt).getTime(),
          closeTime: apiOrder.closeTime ? new Date(apiOrder.closeTime).getTime() : undefined,
          closePrice: apiOrder.closePrice || undefined,
          stopLoss: apiOrder.stopLoss || undefined,
          takeProfit: apiOrder.takeProfit || undefined,
          stopPrice: apiOrder.stopPrice || undefined,
          positionStatus: apiOrder.status,
        } as any;
        
        // ✅ 添加扩展字段（使用 any 类型避免类型错误）
        (order as any).closedQuantity = apiOrder.closedQuantity || 0;
        (order as any).pnl = apiOrder.pnl || 0;
        (order as any).brokerId = apiOrder.brokerId || null;
        (order as any).isHedgeOrder = apiOrder.isHedgeOrder || false;
        (order as any).clientOrderId = apiOrder.clientOrderId || null;
        (order as any).originalType = apiOrder.originalType || null;
        
        // ✅ 添加平仓记录
        if (apiOrder.closeRecords && Array.isArray(apiOrder.closeRecords)) {
          (order as any).closeRecords = apiOrder.closeRecords.map((record: any) => ({
            id: record.id,
            orderId: record.orderId,
            quantity: record.quantity,
            closePrice: record.closePrice,
            pnl: record.pnl,
            fee: record.fee,
            closedAt: new Date(record.closedAt).getTime(),
          }));
        }
        
        this.orders.set(order.id, order);
      });
      
      // 更新索引
      this.updateIndex();
      
      logger.log(`✅ [ClientOrderManager] 已从后端加载 ${this.orders.size} 条客户订单`);
    } catch (error: any) {
      logger.error('❌ [ClientOrderManager] 从后端获取客户订单失败:', error);
      throw error;
    }
  }

  /**
   * 添加客户订单
   */
  addOrder(order: Order): void {
    if (!order.uid) {
      logger.warn('❌ 客户订单缺少 uid，跳过保存');
      return;
    }
    
    // 保存到内存
    this.orders.set(order.id, order);
    
    // 持久化到 localStorage
    this.saveOrder(order);
    
    // 更新索引
    this.updateIndex();
    
    logger.log(`✅ 客户订单已保存: ${order.id} (UID: ${order.uid}, Broker: ${order.brokerUid})`);
  }

  /**
   * 更新客户订单
   */
  updateOrder(orderId: string, updates: Partial<Order>): void {
    const order = this.orders.get(orderId);
    if (!order) {
      logger.warn(`❌ 客户订单不存在，无法更新: ${orderId}`);
      return;
    }
    
    // 合并更新
    Object.assign(order, updates);
    order.updateTime = Date.now();
    
    // 持久化
    this.saveOrder(order);
    
    logger.log(`🔄 客户订单已更新: ${orderId}`, updates);
  }

  /**
   * 获取单个订单
   */
  getOrder(orderId: string): Order | undefined {
    return this.orders.get(orderId);
  }

  /**
   * 获取所有订单
   */
  getAllOrders(brokerUid?: string): Order[] {
    const allOrders = Array.from(this.orders.values());
    if (brokerUid) {
      return allOrders.filter(order => order.brokerUid === brokerUid);
    }
    return allOrders;
  }

  /**
   * 按客户UID获取订单
   */
  getOrdersByClientUid(clientUid: string): Order[] {
    return Array.from(this.orders.values()).filter(order => order.uid === clientUid);
  }

  /**
   * 按交易商UID获取所有客户订单
   */
  getOrdersByBrokerUid(brokerUid: string): Order[] {
    return Array.from(this.orders.values()).filter(order => order.brokerUid === brokerUid);
  }

  /**
   * 获取未平仓订单
   */
  getOpenOrders(brokerUid?: string): Order[] {
    return this.getAllOrders(brokerUid).filter(order => 
      order.status === 'filled' && !order.closeTime
    );
  }

  /**
   * 获取已平仓订单
   */
  getClosedOrders(brokerUid?: string): Order[] {
    return this.getAllOrders(brokerUid).filter(order => 
      order.closeTime !== undefined
    );
  }

  /**
   * 清除所有订单
   */
  clearAll(): void {
    // 清除内存
    this.orders.clear();
    
    // 清除 localStorage
    const index = storage.get<string[]>(INDEX_KEY) || [];
    index.forEach(id => storage.remove(STORAGE_KEY_PREFIX + id));
    storage.remove(INDEX_KEY);
    
    logger.log('🗑️ 所有客户订单已清除');
  }

  /**
   * 保存单个订单到 localStorage
   */
  private saveOrder(order: Order): void {
    storage.set(STORAGE_KEY_PREFIX + order.id, order);
  }

  /**
   * 更新订单索引
   */
  private updateIndex(): void {
    const orderIds = Array.from(this.orders.keys());
    storage.set(INDEX_KEY, orderIds);
  }

  /**
   * 从 localStorage 加载所有订单
   */
  private loadOrders(): void {
    const index = storage.get<string[]>(INDEX_KEY) || [];
    
    index.forEach(id => {
      const order = storage.get<Order>(STORAGE_KEY_PREFIX + id);
      if (order) {
        this.orders.set(order.id, order);
      }
    });
    
    logger.log(`📈 已加载 ${this.orders.size} 个客户订单`);
  }

  /**
   * 获取统计信息
   */
  getStatistics(brokerUid?: string): {
    total: number;
    open: number;
    closed: number;
    cancelled: number;
  } {
    const orders = brokerUid ? this.getOrdersByBrokerUid(brokerUid) : this.getAllOrders();
    
    return {
      total: orders.length,
      open: orders.filter(o => o.status === 'filled' && !o.closeTime).length,
      closed: orders.filter(o => o.closeTime !== undefined).length,
      cancelled: orders.filter(o => o.status === 'cancelled').length,
    };
  }
}

